import styled from 'styled-components'
import { TdsButton, TdsIcon } from '@scania/tegel-react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { Size2d } from '../../../types/Size2d'
import { useRef } from 'react'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import { useRfqEysButtonState } from '../RfqEysButtonState'
import { buildRelativeSummaryPage } from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { getMarketLanguageState } from '../../../store/appSlice'
import {
  getGuidedViewPage,
  setSummaryOrRfqOpenedFromPage,
} from '../../../store/sessionDataSlice'
import { TestElementTypeId } from '../../../types/TestAttributeId'

const FooterRoot = styled.footer`
  width: 100%;
  height: 81px;
  display: flex;
  justify-content: end;
  align-items: center;
  border-top: 1px solid var(--tds-grey-300);
  gap: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 65px;
  }
`

const ButtonsWrapper = styled.div`
  width: 33%;
  display: flex;
  gap: 8px 16px;
  justify-content: space-between;
  padding-right: 16px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    width: 100%;
    padding-right: 16px;
    justify-content: end;
  }
`

const ButtonWrapper = styled.div`
  min-width: 200px;
  width: 50%;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px) {
    width: unset;
  }
`

export interface AllViewFooterProps {
  handleRfqOrEysClick: () => void
  parentSize: Size2d
}

export function AllViewFooter({
  handleRfqOrEysClick,
  parentSize,
}: AllViewFooterProps): JSX.Element {
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)

  const handleSummaryClick = () => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    dispatch(setSummaryOrRfqOpenedFromPage(guidedViewPage))
    const relativeUrl = buildRelativeSummaryPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
  }

  return (
    <FooterRoot data-name="GuidedViewFooter" ref={rootRef}>
      <ButtonsWrapper>
        {!rfqOrEysButtonHidden && (
          <ButtonWrapper>
            <TdsButton
              variant="primary"
              text={rfqOrEysButtonText}
              size={
                parentSize.width < BreakpointWidthPx.Large ||
                parentSize.height + 64 < BreakpointHeightPx.Large
                  ? 'sm'
                  : 'lg'
              }
              fullbleed
              onClick={handleRfqOrEysClick}
              disabled={!rfqOrEysButtonEnabled}
              data-test-element-type={
                TestElementTypeId.AllViewGetConsultationButton
              }
            >
              <TdsIcon slot="icon" size="20px" name="message"></TdsIcon>
            </TdsButton>
          </ButtonWrapper>
        )}
        <ButtonWrapper>
          <TdsButton
            variant="secondary"
            text={t2(TextId.LABEL_ACTION_SUMMARY)}
            size={
              parentSize.width < BreakpointWidthPx.Large ||
              parentSize.height + 64 < BreakpointHeightPx.Large
                ? 'sm'
                : 'lg'
            }
            fullbleed
            onClick={handleSummaryClick}
            data-test-element-type={TestElementTypeId.AllViewSummaryButton}
          >
            <TdsIcon slot="icon" size="20px" name="document"></TdsIcon>
          </TdsButton>
        </ButtonWrapper>
      </ButtonsWrapper>
    </FooterRoot>
  )
}
